/**
 * Meal Domain Service
 *
 * Domain service for meal plan operations in the BiteWise healthcare provider platform.
 * Handles meal plan creation, retrieval, assignment, and management with enhanced error handling,
 * retry logic, and request deduplication.
 *
 * @module services/domains/MealDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager } from '../shared';
import type {
  MealPlan,
  MealPlanFrontend,
  CreateMealPlanRequest,
  CreateMealPlanRequestFrontend,
  MealPlansListResponse,
  AssignMealPlanRequest,
  AssignMealPlanRequestFrontend,
  AssignMealPlanResponse,
  AssignMealPlanResponseFrontend,
  UpdateAssignmentStatusRequest,
  GetMealPlansParams,
  GetPatientMealPlansParams,
  MealPlanAssignmentStatus,
  UpdateMealRequest,
  UpdateMealResponse,
  RegenerateMealRequest,
  RegenerateMealResponse,
  SwapMealRequest,
  SwapMealResponse,
  MealFrontend
} from '@/types/mealPlan';

/**
 * Meal domain-specific error class
 */
export class MealApiError extends BaseApiError {
  /** The operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'MealApiError';
    this.operation = operation;
  }
}

/**
 * Meal API endpoints
 */
const ENDPOINTS = {
  MEAL_PLANS: '/mealplan/',
  MEAL_PLAN_BY_ID: (id: string) => `/mealplan/${id}`,
  ASSIGN_MEAL_PLAN: (id: string) => `/mealplan/${id}/assign`,
  PATIENT_MEAL_PLANS: (patientId: string) => `/mealplan/patient/${patientId}`,
  ASSIGNMENT_STATUS: (assignmentId: string) => `/mealplan/assignment/${assignmentId}/status`,
  REMOVE_ASSIGNMENT: (assignmentId: string) => `/mealplan/assignment/${assignmentId}`,
  UPDATE_MEAL: (mealPlanId: string, mealId: string) => `/mealplan/${mealPlanId}/meals/${mealId}`,
  REGENERATE_MEAL: (mealPlanId: string, mealId: string) => `/mealplan/${mealPlanId}/meals/${mealId}/regenerate`,
  SWAP_MEAL: (mealPlanId: string, mealId: string) => `/mealplan/${mealPlanId}/meals/${mealId}/swap`,
} as const;

/**
 * Meal Domain Service
 *
 * Provides meal plan operations with enhanced error handling,
 * retry logic, and request deduplication.
 */
export class MealDomain extends BaseDomain<MealApiError> {
  constructor() {
    const apiClient = new ApiClient<MealApiError>(
      (message, status, fieldErrors, code) => new MealApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }


  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'Meal';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): MealApiError {
    return new MealApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Create a new meal plan
   *
   * @param mealPlanData Meal plan creation parameters
   * @returns Promise<MealPlanFrontend> Created meal plan data
   * @throws MealApiError When request fails or validation errors occur
   */
  async createMealPlan(mealPlanData: CreateMealPlanRequestFrontend): Promise<MealPlanFrontend> {
    return this.executeWithRetry(async () => {
      try {

        const requestData = this.transformCreateMealPlanRequest(mealPlanData);
        const response = await this.apiClient.post<MealPlan>(
          ENDPOINTS.MEAL_PLANS,
          requestData
        );

        const transformedResponse = this.transformMealPlanResponse(response);

        return transformedResponse;
      } catch (error) {
        if (error instanceof MealApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'createMealPlan',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'createMealPlan',
              'Only dieticians can create meal plans.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'createMealPlan',
              'Invalid meal plan data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
        }
        throw error;
      }
    }, {
      maxRetries: 0,
    });
  }

  /**
   * Get a specific meal plan by ID
   *
   * @param mealPlanId UUID of the meal plan
   * @returns Promise<MealPlanFrontend> Meal plan data
   * @throws MealApiError When request fails or meal plan not found
   */
  async getMealPlan(mealPlanId: string): Promise<MealPlanFrontend> {
    return this.executeWithDeduplication(`getMealPlan:${mealPlanId}`, async () => {
      return this.executeWithRetry(async () => {
        try {

          const response = await this.apiClient.get<MealPlan>(
            ENDPOINTS.MEAL_PLAN_BY_ID(mealPlanId)
          );

          const transformedResponse = this.transformMealPlanResponse(response);

          return transformedResponse;
        } catch (error) {
          if (error instanceof MealApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getMealPlan',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 403) {
              throw this.createError(
                'getMealPlan',
                'No permission to access this meal plan.',
                403,
                undefined,
                'FORBIDDEN'
              );
            }
            if (error.status === 404) {
              throw this.createError(
                'getMealPlan',
                'Meal plan not found.',
                404,
                undefined,
                'MEAL_PLAN_NOT_FOUND'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Get meal plans for the current user with pagination
   *
   * @param params Pagination parameters
   * @returns Promise<{ mealPlans: MealPlanFrontend[], total: number, page: number, perPage: number, totalPages: number }> Paginated meal plans
   * @throws MealApiError When request fails
   */
  async getMealPlans(params: GetMealPlansParams = {}): Promise<{
    mealPlans: MealPlanFrontend[];
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  }> {
    return this.executeWithDeduplication(`getMealPlans:${JSON.stringify(params)}`, async () => {
      return this.executeWithRetry(async () => {
        try {

          const queryParams = new URLSearchParams();
          if (params.page) queryParams.append('page', params.page.toString());
          if (params.per_page) queryParams.append('per_page', params.per_page.toString());

          const url = queryParams.toString()
            ? `${ENDPOINTS.MEAL_PLANS}?${queryParams.toString()}`
            : ENDPOINTS.MEAL_PLANS;

          const response = await this.apiClient.get<MealPlansListResponse>(url);

          const transformedMealPlans = response.meal_plans.map(mealPlan =>
            this.transformMealPlanResponse(mealPlan)
          );


          return {
            mealPlans: transformedMealPlans,
            total: response.total,
            page: response.page,
            perPage: response.per_page,
            totalPages: response.total_pages
          };
        } catch (error) {
          if (error instanceof MealApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getMealPlans',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Delete a meal plan
   *
   * @param mealPlanId UUID of the meal plan to delete
   * @returns Promise<void>
   * @throws MealApiError When request fails or meal plan not found
   */
  async deleteMealPlan(mealPlanId: string): Promise<void> {
    return this.executeWithRetry(async () => {
      try {

        await this.apiClient.delete(ENDPOINTS.MEAL_PLAN_BY_ID(mealPlanId));

      } catch (error) {
        if (error instanceof MealApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'deleteMealPlan',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'deleteMealPlan',
              'Only the creator can delete the meal plan.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'deleteMealPlan',
              'Meal plan not found.',
              404,
              undefined,
              'MEAL_PLAN_NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Assign a meal plan to a patient
   *
   * @param mealPlanId UUID of the meal plan to assign
   * @param assignmentData Assignment parameters
   * @returns Promise<AssignMealPlanResponseFrontend> Assignment response
   * @throws MealApiError When request fails or validation errors occur
   */
  async assignMealPlan(
    mealPlanId: string,
    assignmentData: Omit<AssignMealPlanRequestFrontend, 'mealPlanId'>
  ): Promise<AssignMealPlanResponseFrontend> {
    return this.executeWithRetry(async () => {
      try {

        const requestData = this.transformAssignMealPlanRequest({
          ...assignmentData,
          mealPlanId
        });

        const response = await this.apiClient.post<AssignMealPlanResponse>(
          ENDPOINTS.ASSIGN_MEAL_PLAN(mealPlanId),
          requestData
        );

        const transformedResponse = this.transformAssignMealPlanResponse(response);

        return transformedResponse;
      } catch (error) {
        if (error instanceof MealApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'assignMealPlan',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'assignMealPlan',
              'Only dieticians can assign meal plans.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'assignMealPlan',
              'Invalid assignment data or meal plan ID mismatch.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'assignMealPlan',
              'Meal plan or patient not found.',
              404,
              undefined,
              'NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Get meal plans assigned to a specific patient
   *
   * @param patientId UUID of the patient
   * @param params Filter parameters
   * @returns Promise<AssignMealPlanResponseFrontend[]> Patient's meal plan assignments
   * @throws MealApiError When request fails
   */
  async getPatientMealPlans(
    patientId: string,
    params: GetPatientMealPlansParams = {}
  ): Promise<AssignMealPlanResponseFrontend[]> {
    return this.executeWithDeduplication(`getPatientMealPlans:${patientId}:${JSON.stringify(params)}`, async () => {
      return this.executeWithRetry(async () => {
        try {

          const queryParams = new URLSearchParams();
          if (params.status) queryParams.append('status', params.status);

          const url = queryParams.toString()
            ? `${ENDPOINTS.PATIENT_MEAL_PLANS(patientId)}?${queryParams.toString()}`
            : ENDPOINTS.PATIENT_MEAL_PLANS(patientId);

          const response = await this.apiClient.get<AssignMealPlanResponse[]>(url);

          const transformedResponse = response.map(assignment =>
            this.transformAssignMealPlanResponse(assignment)
          );


          return transformedResponse;
        } catch (error) {
          if (error instanceof MealApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getPatientMealPlans',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 403) {
              throw this.createError(
                'getPatientMealPlans',
                'Only dieticians can access patient meal plans.',
                403,
                undefined,
                'FORBIDDEN'
              );
            }
            if (error.status === 404) {
              throw this.createError(
                'getPatientMealPlans',
                'Patient not found.',
                404,
                undefined,
                'PATIENT_NOT_FOUND'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Update assignment status
   *
   * @param assignmentId UUID of the assignment
   * @param status New assignment status
   * @returns Promise<AssignMealPlanResponseFrontend> Updated assignment
   * @throws MealApiError When request fails
   */
  async updateAssignmentStatus(
    assignmentId: string,
    status: MealPlanAssignmentStatus
  ): Promise<AssignMealPlanResponseFrontend> {
    return this.executeWithRetry(async () => {
      try {

        const requestData: UpdateAssignmentStatusRequest = { status };
        const response = await this.apiClient.put<AssignMealPlanResponse>(
          ENDPOINTS.ASSIGNMENT_STATUS(assignmentId),
          requestData
        );

        const transformedResponse = this.transformAssignMealPlanResponse(response);

        return transformedResponse;
      } catch (error) {
        if (error instanceof MealApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'updateAssignmentStatus',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'updateAssignmentStatus',
              'No permission to update this assignment.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'updateAssignmentStatus',
              'Invalid status value.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'updateAssignmentStatus',
              'Assignment not found.',
              404,
              undefined,
              'ASSIGNMENT_NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Remove patient assignment from meal plan
   *
   * @param assignmentId UUID of the assignment to remove
   * @returns Promise<void>
   * @throws MealApiError When request fails
   */
  async removeAssignment(assignmentId: string): Promise<void> {
    return this.executeWithRetry(async () => {
      try {

        await this.apiClient.delete(ENDPOINTS.REMOVE_ASSIGNMENT(assignmentId));

      } catch (error) {
        if (error instanceof MealApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'removeAssignment',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'removeAssignment',
              'Only dieticians can remove assignments.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'removeAssignment',
              'Assignment not found.',
              404,
              undefined,
              'ASSIGNMENT_NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Update individual meal nutrition and name
   *
   * @param mealPlanId UUID of the meal plan
   * @param mealId UUID of the meal to update
   * @param data Updated meal data
   * @returns Promise<MealFrontend> Updated meal data
   * @throws MealApiError When request fails or validation errors occur
   */
  async updateMeal(
    mealPlanId: string,
    mealId: string,
    data: UpdateMealRequest
  ): Promise<MealFrontend> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.put<UpdateMealResponse>(
          ENDPOINTS.UPDATE_MEAL(mealPlanId, mealId),
          data
        );

        const transformedMeal = this.transformMealResponse(response.meal);
        return transformedMeal;
      } catch (error) {
        if (error instanceof MealApiError) {
          if (error.status === 401) {
            throw this.createError(
              'updateMeal',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'updateMeal',
              'Only the meal plan creator can edit meals.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'updateMeal',
              'Invalid meal data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'updateMeal',
              'Meal or meal plan not found.',
              404,
              undefined,
              'NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Regenerate meal with AI guidance
   *
   * @param mealPlanId UUID of the meal plan
   * @param mealId UUID of the meal to regenerate
   * @param data Regeneration parameters with guidance
   * @returns Promise<MealFrontend> Regenerated meal data
   * @throws MealApiError When request fails or validation errors occur
   */
  async regenerateMeal(
    mealPlanId: string,
    mealId: string,
    data: RegenerateMealRequest
  ): Promise<MealFrontend> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<RegenerateMealResponse>(
          ENDPOINTS.REGENERATE_MEAL(mealPlanId, mealId),
          data
        );

        const transformedMeal = this.transformMealResponse(response.meal);
        return transformedMeal;
      } catch (error) {
        if (error instanceof MealApiError) {
          if (error.status === 401) {
            throw this.createError(
              'regenerateMeal',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'regenerateMeal',
              'Only the meal plan creator can regenerate meals.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'regenerateMeal',
              'Invalid guidance data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'regenerateMeal',
              'Meal or meal plan not found.',
              404,
              undefined,
              'NOT_FOUND'
            );
          }
          if (error.status === 500) {
            throw this.createError(
              'regenerateMeal',
              'Meal generation failed. Please try again.',
              500,
              undefined,
              'GENERATION_ERROR'
            );
          }
        }
        throw error;
      }
    }, {
      maxRetries: 0,
    });
  }

  /**
   * Swap meal with new AI-generated meal
   *
   * @param mealPlanId UUID of the meal plan
   * @param mealId UUID of the meal to swap
   * @param data Swap parameters with special instructions
   * @returns Promise<{ mealPlan: MealPlanFrontend; swappedMeal: MealFrontend }> Updated meal plan and swapped meal
   * @throws MealApiError When request fails or validation errors occur
   */
  async swapMeal(
    mealPlanId: string,
    mealId: string,
    data: SwapMealRequest
  ): Promise<{ mealPlan: MealPlanFrontend; swappedMeal: MealFrontend }> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<SwapMealResponse>(
          ENDPOINTS.SWAP_MEAL(mealPlanId, mealId),
          data
        );

        const transformedMealPlan = this.transformMealPlanResponse(response.meal_plan);
        const transformedSwappedMeal = this.transformMealResponse(response.swapped_meal);

        return {
          mealPlan: transformedMealPlan,
          swappedMeal: transformedSwappedMeal,
        };
      } catch (error) {
        if (error instanceof MealApiError) {
          if (error.status === 401) {
            throw this.createError(
              'swapMeal',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 403) {
            throw this.createError(
              'swapMeal',
              'Only the meal plan creator can swap meals.',
              403,
              undefined,
              'FORBIDDEN'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'swapMeal',
              'Invalid swap data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'swapMeal',
              'Meal or meal plan not found.',
              404,
              undefined,
              'NOT_FOUND'
            );
          }
          if (error.status === 422) {
            throw this.createError(
              'swapMeal',
              'Validation failed. Please check your input.',
              422,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 500) {
            throw this.createError(
              'swapMeal',
              'Meal generation failed. Please try again.',
              500,
              undefined,
              'GENERATION_ERROR'
            );
          }
        }
        throw error;
      }
    }, {
      maxRetries: 0,
    });
  }

  /**
   * Transform Meal API response to frontend format (snake_case to camelCase)
   *
   * @private
   */
  private transformMealResponse(apiMeal: UpdateMealResponse['meal'] | RegenerateMealResponse['meal']): MealFrontend {
    return {
      id: apiMeal.id,
      mealName: apiMeal.meal_name,
      mealType: apiMeal.meal_type,
      cuisine: apiMeal.cuisine,
      prepTimeMinutes: apiMeal.prep_time_minutes,
      cookTimeMinutes: apiMeal.cook_time_minutes,
      servings: apiMeal.servings,
      difficulty: apiMeal.difficulty,
      ingredients: apiMeal.ingredients.map(ingredient => ({
        name: ingredient.name,
        amount: ingredient.amount,
        unit: ingredient.unit,
        costAud: ingredient.cost_aud,
      })),
      instructions: apiMeal.instructions,
      nutrition: {
        caloriesPerServing: apiMeal.nutrition.calories_per_serving,
        proteinG: apiMeal.nutrition.protein_g,
        carbsG: apiMeal.nutrition.carbs_g,
        fatG: apiMeal.nutrition.fat_g,
        fiberG: apiMeal.nutrition.fiber_g,
        sugarG: apiMeal.nutrition.sugar_g,
        sodiumMg: apiMeal.nutrition.sodium_mg,
        caloriesPer100g: apiMeal.nutrition.calories_per_100g,
      },
      tags: apiMeal.tags,
      estimatedCostAud: apiMeal.estimated_cost_aud,
    };
  }

  /**
   * Transform CreateMealPlanRequestFrontend to API format (camelCase to snake_case)
   *
   * @private
   */
  private transformCreateMealPlanRequest(request: CreateMealPlanRequestFrontend): CreateMealPlanRequest {
    return {
      meal_count: request.mealCount ?? request.mealTypes.length,
      meal_types: request.mealTypes,
      dietary_restrictions: request.dietaryRestrictions,
      allergies: request.allergies,
      cuisine_preferences: request.cuisinePreferences,
      disliked_ingredients: request.dislikedIngredients,
      budget_aud: request.budgetAud,
      servings_per_meal: request.servingsPerMeal,
      label: request.label,
      max_prep_time: request.maxPrepTime,
      health_conditions: request.healthConditions,
      user_guidance: request.userGuidance
    };
  }

  /**
   * Transform AssignMealPlanRequestFrontend to API format (camelCase to snake_case)
   *
   * @private
   */
  private transformAssignMealPlanRequest(request: AssignMealPlanRequestFrontend): AssignMealPlanRequest {
    return {
      meal_plan_id: request.mealPlanId,
      patient_id: request.patientId,
      notes: request.notes
    };
  }

  /**
   * Transform MealPlan API response to frontend format (snake_case to camelCase)
   *
   * @private
   */
  private transformMealPlanResponse(apiResponse: MealPlan): MealPlanFrontend {
    return {
      id: apiResponse.id,
      creatorId: apiResponse.creator_id,
      creatorName: apiResponse.creator_name,
      label: apiResponse.label,
      totalEstimatedCostAud: apiResponse.total_estimated_cost_aud,
      planDurationDays: apiResponse.plan_duration_days,
      mealCount: apiResponse.meal_count,
      creationParameters: {
        mealCount: apiResponse.creation_parameters.meal_count,
        mealTypes: apiResponse.creation_parameters.meal_types,
        dietaryRestrictions: apiResponse.creation_parameters.dietary_restrictions,
        allergies: apiResponse.creation_parameters.allergies,
        cuisinePreferences: apiResponse.creation_parameters.cuisine_preferences,
        budgetAud: apiResponse.creation_parameters.budget_aud,
        servingsPerMeal: apiResponse.creation_parameters.servings_per_meal,
        maxPrepTime: apiResponse.creation_parameters.max_prep_time,
        healthConditions: apiResponse.creation_parameters.health_conditions
      },
      mealPlanData: {
        meals: apiResponse.meal_plan_data.meals.map(meal => ({
          id: meal.id,
          mealName: meal.meal_name,
          mealType: meal.meal_type,
          cuisine: meal.cuisine,
          prepTimeMinutes: meal.prep_time_minutes,
          cookTimeMinutes: meal.cook_time_minutes,
          servings: meal.servings,
          difficulty: meal.difficulty,
          ingredients: meal.ingredients.map(ingredient => ({
            name: ingredient.name,
            amount: ingredient.amount,
            unit: ingredient.unit,
            costAud: ingredient.cost_aud
          })),
          instructions: meal.instructions,
          nutrition: {
            caloriesPerServing: meal.nutrition.calories_per_serving,
            proteinG: meal.nutrition.protein_g,
            carbsG: meal.nutrition.carbs_g,
            fatG: meal.nutrition.fat_g,
            fiberG: meal.nutrition.fiber_g,
            sugarG: meal.nutrition.sugar_g,
            sodiumMg: meal.nutrition.sodium_mg,
            caloriesPer100g: meal.nutrition.calories_per_100g
          },
          tags: meal.tags,
          estimatedCostAud: meal.estimated_cost_aud
        })),
        totalEstimatedCostAud: apiResponse.meal_plan_data.total_estimated_cost_aud,
        planDurationDays: apiResponse.meal_plan_data.plan_duration_days,
        generatedAt: apiResponse.meal_plan_data.generated_at
      },
      performanceData: {
        totalGenerationTimeMs: apiResponse.performance_data.total_generation_time_ms,
        totalTokensUsed: apiResponse.performance_data.total_tokens_used,
        totalTokensPrompt: apiResponse.performance_data.total_tokens_prompt,
        totalTokensCompletion: apiResponse.performance_data.total_tokens_completion,
        totalApiCalls: apiResponse.performance_data.total_api_calls,
        successfulCalls: apiResponse.performance_data.successful_calls,
        failedCalls: apiResponse.performance_data.failed_calls,
        retryAttempts: apiResponse.performance_data.retry_attempts,
        circuitBreakerTrips: apiResponse.performance_data.circuit_breaker_trips,
        rateLimitBlocks: apiResponse.performance_data.rate_limit_blocks,
        parallelMealsGenerated: apiResponse.performance_data.parallel_meals_generated,
        mealsRequiringRefinement: apiResponse.performance_data.meals_requiring_refinement,
        avgMealGenerationTimeMs: apiResponse.performance_data.avg_meal_generation_time_ms,
        evaluationMethod: apiResponse.performance_data.evaluation_method,
        evaluationFailures: apiResponse.performance_data.evaluation_failures,
        evaluationSuccessRate: apiResponse.performance_data.evaluation_success_rate,
        concurrentEvaluations: apiResponse.performance_data.concurrent_evaluations,
        conceptGenerationEnabled: apiResponse.performance_data.concept_generation_enabled,
        conceptGenerationTimeMs: apiResponse.performance_data.concept_generation_time_ms,
        conceptsGenerated: apiResponse.performance_data.concepts_generated,
        conceptGenerationFailures: apiResponse.performance_data.concept_generation_failures,
        diversityScore: apiResponse.performance_data.diversity_score,
        finalDiversityScore: apiResponse.performance_data.final_diversity_score,
        cookingMethodDistribution: apiResponse.performance_data.cooking_method_distribution,
        cuisineDistribution: apiResponse.performance_data.cuisine_distribution,
        diversityConstraintsMet: apiResponse.performance_data.diversity_constraints_met,
        uniqueCookingMethods: apiResponse.performance_data.unique_cooking_methods,
        uniqueCuisines: apiResponse.performance_data.unique_cuisines,
        prepTimeCategory: apiResponse.performance_data.prep_time_category,
        prepTimeComplianceRate: apiResponse.performance_data.prep_time_compliance_rate,
        prepTimeRefinements: apiResponse.performance_data.prep_time_refinements,
        avgTimeReductionMinutes: apiResponse.performance_data.avg_time_reduction_minutes,
        apiCallDetails: apiResponse.performance_data.api_call_details?.map(detail => ({
          operation: detail.operation,
          startTime: detail.start_time,
          endTime: detail.end_time,
          durationMs: detail.duration_ms,
          tokensUsed: detail.tokens_used,
          tokensPrompt: detail.tokens_prompt,
          tokensCompletion: detail.tokens_completion,
          model: detail.model,
          temperature: detail.temperature,
          success: detail.success,
          errorMessage: detail.error_message,
          retryCount: detail.retry_count,
          mealType: detail.meal_type,
          mealIndex: detail.meal_index
        })) || [],
        operationSummary: apiResponse.performance_data.operation_summary ? Object.fromEntries(
          Object.entries(apiResponse.performance_data.operation_summary).map(([key, value]) => [
            key,
            {
              count: value.count,
              successCount: value.success_count,
              totalDurationMs: value.total_duration_ms,
              totalTokens: value.total_tokens,
              avgDurationMs: value.avg_duration_ms,
              successRate: value.success_rate
            }
          ])
        ) : {}
      },
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
      generationTimeMs: apiResponse.generation_time_ms,
      sdkVersion: apiResponse.sdk_version,
      totalTokenUsage: apiResponse.total_token_usage,
      assignmentInfo: apiResponse.assignment_info ? {
        id: apiResponse.assignment_info.id,
        patientId: apiResponse.assignment_info.patient_id,
        patientName: apiResponse.assignment_info.patient_name,
        assignedById: apiResponse.assignment_info.assigned_by_id,
        assignedByName: apiResponse.assignment_info.assigned_by_name,
        assignedAt: apiResponse.assignment_info.assigned_at,
        notes: apiResponse.assignment_info.notes,
        status: apiResponse.assignment_info.status
      } : undefined
    };
  }

  /**
   * Transform AssignMealPlanResponse API response to frontend format (snake_case to camelCase)
   *
   * @private
   */
  private transformAssignMealPlanResponse(apiResponse: AssignMealPlanResponse): AssignMealPlanResponseFrontend {
    return {
      id: apiResponse.id,
      mealPlanId: apiResponse.meal_plan_id,
      patientId: apiResponse.patient_id,
      patientName: apiResponse.patient_name,
      assignedById: apiResponse.assigned_by_id,
      assignedByName: apiResponse.assigned_by_name,
      assignedAt: apiResponse.assigned_at,
      notes: apiResponse.notes,
      status: apiResponse.status,
      mealPlan: this.transformMealPlanResponse(apiResponse.meal_plan)
    };
  }
}

/**
 * Singleton instance of MealDomain
 */
export const mealDomain = new MealDomain();